import type {
  MimirChannel,
  MimirChannels,
  MimirOutcome,
  MimirPreview,
} from './types';
import { str } from './wireValues';

// Wire readers for Mimir's sends (OMT-1839). Wire data is untrusted: malformed
// entries are dropped, and a reply that can't be read becomes code 'unknown'.

type Obj = Record<string, unknown>;

const obj = (v: unknown): Obj | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : null;
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const present = <T>(v: T | null): v is T => v !== null;
const strings = (v: unknown): string[] =>
  list(v).filter((x): x is string => typeof x === 'string');

const MODES: readonly string[] = ['all', 'mention', 'test'];

const toChannel = (v: unknown): MimirChannel | null => {
  const o = obj(v);
  const id = o && str(o.id);
  const name = o && str(o.name);
  if (!o || !id || !name) return null;
  const mode = str(o.mode);
  return {
    id,
    name,
    private: o.private === true,
    mode: mode && MODES.includes(mode) ? (mode as MimirChannel['mode']) : null,
  };
};

export function toMimirChannels(body: unknown): MimirChannels | null {
  const o = obj(body);
  const botUserId = o && str(o.botUserId);
  if (!o || !botUserId || !Array.isArray(o.channels)) return null;
  return { botUserId, channels: o.channels.map(toChannel).filter(present) };
}

export function toMimirPreview(body: unknown): MimirPreview | null {
  const o = obj(body);
  const text = o ? str(o.text) : undefined;
  const sha256 = o && str(o.sha256);
  if (!o || text === undefined || !sha256) return null;
  return {
    text,
    sha256,
    poweredBy: str(o.poweredBy) ?? '',
    signed: o.signed === true,
    introVersion: str(o.introVersion) ?? null,
    draft: o.draft === true,
    found: strings(o.found),
  };
}

const OK_CODES: readonly string[] = ['posted', 'already'];

const outcomeOf = (
  body: unknown,
  first: 'outcome' | 'detail',
): MimirOutcome => {
  const o = obj(body);
  const second = first === 'outcome' ? 'detail' : 'outcome';
  const code = (o && (str(o[first]) ?? str(o[second]))) || 'unknown';
  return {
    ok: OK_CODES.includes(code),
    code,
    permalink: (o && str(o.permalink)) ?? null,
    found: o ? strings(o.found) : [],
  };
};

/** A send's reply: the code is in `outcome`, falling back to `detail`. ok only for posted/already. */
export const toMimirSendOutcome = (body: unknown): MimirOutcome =>
  outcomeOf(body, 'outcome');

/** A preview's refusal: the code is in `detail`, falling back to `outcome`. Never ok. */
export const toMimirRefusal = (body: unknown): MimirOutcome => ({
  ...outcomeOf(body, 'detail'),
  ok: false,
});
